import React, { useEffect, useState } from "react"
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import placeEnhancementsService, { PlaceContact } from "@/services/PlaceEnhancementsService"

interface PlaceContactsTabProps {
  placeId: string
  onContactsChange?: () => void
}

const PlaceContactsTab: React.FC<PlaceContactsTabProps> = ({ placeId, onContactsChange }) => {
  const { t } = useTranslation("global")
  const [contacts, setContacts] = useState<PlaceContact[]>(() => placeEnhancementsService.getContacts(placeId))
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [editingContact, setEditingContact] = useState<PlaceContact | null>(null)

  // Form state
  const [name, setName] = useState("")
  const [position, setPosition] = useState("")
  const [phone, setPhone] = useState("")
  const [email, setEmail] = useState("")
  const [isPrimary, setIsPrimary] = useState(false)
  const [notes, setNotes] = useState("")

  const reloadContacts = async () => {
    try {
      setIsLoading(true)
      const list = await placeEnhancementsService.fetchContactsAsync(placeId)
      setContacts(list)
      if (onContactsChange) onContactsChange()
    } catch (ex) {
      console.error(ex)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    reloadContacts()
  }, [placeId])

  const handleOpenAdd = () => {
    setEditingContact(null)
    setName("")
    setPosition("")
    setPhone("")
    setEmail("")
    setIsPrimary(contacts.length === 0)
    setNotes("")
    setShowModal(true)
  }

  const handleOpenEdit = (cnt: PlaceContact) => {
    setEditingContact(cnt)
    setName(cnt.name)
    setPosition(cnt.position || "")
    setPhone(cnt.phone || "")
    setEmail(cnt.email || "")
    setIsPrimary(cnt.isPrimary)
    setNotes(cnt.notes || "")
    setShowModal(true)
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return

    try {
      setIsSaving(true)
      if (editingContact) {
        await placeEnhancementsService.updateContactAsync(editingContact.id, {
          name,
          position,
          phone,
          email,
          isPrimary,
          notes,
        })
      } else {
        await placeEnhancementsService.addContactAsync({
          placeId,
          name,
          position,
          phone,
          email,
          isPrimary,
          notes,
        })
      }
      setShowModal(false)
      await reloadContacts()
    } catch (ex) {
      console.error(ex)
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (window.confirm(t("Are you sure you want to delete this contact?"))) {
      await placeEnhancementsService.deleteContactAsync(id)
      await reloadContacts()
    }
  }

  return (
    <div className="py-2">
      <div className="d-flex justify-content-end align-items-center mb-3">
        <Button variant="primary" size="sm" className="rounded-3 px-3 fw-semibold" onClick={handleOpenAdd}>
          <i className="fas fa-plus me-1" />
          {t("Agregar Contacto")}
        </Button>
      </div>

      {isLoading ? (
        <div className="text-center py-4">
          <Spinner animation="border" size="sm" className="me-2" />
          {t("Cargando contactos...")}
        </div>
      ) : contacts.length === 0 ? (
        <Alert variant="light" className="border text-center py-4 rounded-3">
          <i className="fas fa-user-slash fa-2x mb-2 text-muted" />
          <div className="text-muted small">{t("No hay contactos registrados para este lugar todavía.")}</div>
          <Button variant="outline-primary" size="sm" className="mt-2 rounded-3" onClick={handleOpenAdd}>
            <i className="fas fa-plus me-1" />
            {t("Agregar Primer Contacto")}
          </Button>
        </Alert>
      ) : (
        <div className="d-flex flex-column gap-2">
          {contacts.map(c => {
            const initials = c.name
              ? c.name.split(" ").map(n => n.charAt(0)).join("").substring(0, 2).toUpperCase()
              : "CN"

            return (
              <Card key={c.id} className="border shadow-sm rounded-3 p-3 position-relative bg-white">
                <div className="d-flex align-items-center justify-content-between">
                  {/* LEFT BLOCK: Avatar + Details */}
                  <div className="d-flex align-items-center gap-3 overflow-hidden">
                    {/* Avatar Initials / Image */}
                    <div
                      className="rounded-circle text-white d-flex align-items-center justify-content-center fw-bold shadow-sm"
                      style={{
                        width: "44px",
                        height: "44px",
                        background: "linear-gradient(135deg, #007AFF, #5856D6)",
                        fontSize: "0.95rem",
                        flexShrink: 0
                      }}
                    >
                      {initials}
                    </div>

                    {/* Information Block */}
                    <div className="overflow-hidden">
                      {/* Top Small Line: Position & Primary Badge */}
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <small className="text-muted fw-bold text-uppercase" style={{ fontSize: "0.72rem" }}>
                          {c.position || t("Contacto Clave")}
                        </small>
                        {c.isPrimary && (
                          <Badge bg="warning" text="dark" className="px-1 py-0" style={{ fontSize: "0.65rem" }}>
                            ⭐ {t("Principal")}
                          </Badge>
                        )}
                      </div>

                      {/* Middle Line: Full Name */}
                      <div className="fw-bold text-dark text-truncate" style={{ fontSize: "0.95rem" }}>
                        {c.name}
                      </div>

                      {/* Bottom Line: Phone & Email in the same row */}
                      <div className="d-flex align-items-center gap-3 text-muted small mt-1">
                        {c.phone ? (
                          <a href={`tel:${c.phone}`} className="text-decoration-none text-muted">
                            <i className="fas fa-phone me-1 text-success" />
                            {c.phone}
                          </a>
                        ) : (
                          <span className="text-muted"><i className="fas fa-phone me-1 opacity-50" />—</span>
                        )}

                        {c.email ? (
                          <a href={`mailto:${c.email}`} className="text-decoration-none text-muted text-truncate">
                            <i className="fas fa-envelope me-1 text-primary" />
                            {c.email}
                          </a>
                        ) : (
                          <span className="text-muted"><i className="fas fa-envelope me-1 opacity-50" />—</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* RIGHT BLOCK: Action Icon Buttons (Icon-only without text) */}
                  <div className="d-flex align-items-center gap-1 ms-2 flex-shrink-0">
                    <Button
                      variant="light"
                      size="sm"
                      className="p-1 px-2 rounded-3 border-0 bg-transparent text-secondary"
                      title={t("Edit")}
                      onClick={() => handleOpenEdit(c)}
                    >
                      <i className="fas fa-pen" />
                    </Button>
                    <Button
                      variant="light"
                      size="sm"
                      className="p-1 px-2 rounded-3 border-0 bg-transparent text-danger"
                      title={t("Delete")}
                      onClick={() => handleDelete(c.id)}
                    >
                      <i className="fas fa-trash" />
                    </Button>
                  </div>
                </div>
              </Card>
            )
          })}
        </div>
      )}

      {/* Add / Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Form onSubmit={handleSave}>
          <Modal.Header closeButton>
            <Modal.Title className="fs-6 fw-bold">
              {editingContact ? t("Edit Contact") : t("Add New Contact")}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="small">
            <Row className="g-3">
              <Col xs={12}>
                <Form.Label className="fw-semibold">{t("Full Name")} *</Form.Label>
                <Form.Control
                  size="sm"
                  required
                  placeholder={t("e.g. Juan Pérez")}
                  value={name}
                  onChange={e => setName(e.target.value)}
                />
              </Col>
              <Col xs={6}>
                <Form.Label className="fw-semibold">{t("Position / Role")}</Form.Label>
                <Form.Control
                  size="sm"
                  placeholder={t("e.g. Store Manager")}
                  value={position}
                  onChange={e => setPosition(e.target.value)}
                />
              </Col>
              <Col xs={6}>
                <Form.Label className="fw-semibold">{t("Phone")}</Form.Label>
                <Form.Control
                  size="sm"
                  placeholder={t("+1 809 555 1234")}
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                />
              </Col>
              <Col xs={12}>
                <Form.Label className="fw-semibold">{t("Email")}</Form.Label>
                <Form.Control
                  size="sm"
                  type="email"
                  placeholder="contact@example.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                />
              </Col>
              <Col xs={12}>
                <Form.Check
                  type="checkbox"
                  id="chk-primary-contact"
                  label={t("Set as Primary Contact for this place")}
                  checked={isPrimary}
                  onChange={e => setIsPrimary(e.target.checked)}
                />
              </Col>
              <Col xs={12}>
                <Form.Label className="fw-semibold">{t("Notes")}</Form.Label>
                <Form.Control
                  size="sm"
                  as="textarea"
                  rows={2}
                  placeholder={t("Additional schedule or contact preferences...")}
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer className="border-0 pt-0">
            <Button variant="primary" size="sm" type="submit" className="w-100 py-2 fw-semibold rounded-3">
              {t("Save Contact")}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  )
}

export default PlaceContactsTab
